import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch, useSelector } from '@tabeeb/store'
import {
  ContentSharedLinkKeys,
  generateContentSharedLinkRequest,
  getContentSharedLinkRequest,
  GetContentSharedLinkRequestParams,
  introduceContentSharedLinkRequest,
  regenerateContentSharedLinkRequest,
  revokeContentSharedLinkRequest,
} from '@tabeeb/modules/contentSharedLinks/requests'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { getCurrentUserTenantId } from '@tabeeb/modules/account/selectors'
import { getContentId } from '@tabeeb/shared/content/selectors'

export const useSharedContentLinks = (enabled: boolean) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const currentTenantId = useSelector(getCurrentUserTenantId)
  const currentContentId = useSelector(getContentId)
  const payload = useMemo<GetContentSharedLinkRequestParams>(
    () => ({
      contentId: currentContentId,
      tenantId: currentTenantId,
    }),
    [currentContentId, currentTenantId]
  )
  const query = useQuery({
    queryKey: [ContentSharedLinkKeys.contentSharedLink, payload],
    queryFn: getContentSharedLinkRequest,
    enabled,
  })

  const handleMutateError = useCallback(
    (message) => {
      dispatch(onAddErrorNotification({ message }))
    },
    [dispatch]
  )

  const handleMutateSuccess = useCallback(
    (message) => {
      dispatch(onAddSuccessNotification({ message }))
      queryClient.invalidateQueries({ queryKey: [ContentSharedLinkKeys.contentSharedLink] })
    },
    [dispatch, queryClient]
  )

  const generateContentSharedLinkMutation = useMutation({
    mutationFn: generateContentSharedLinkRequest,
    onError: () => {
      handleMutateError('Failed to generate the link')
    },
    onSuccess: () => {
      handleMutateSuccess('Link was successfully generated')
    },
  })

  const regenerateContentSharedLinkMutation = useMutation({
    mutationFn: regenerateContentSharedLinkRequest,
    onError: () => {
      handleMutateError('Failed to regenerate the link')
    },
    onSuccess: () => {
      handleMutateSuccess('Link was successfully regenerated')
    },
  })

  const revokeContentSharedLinkMutation = useMutation({
    mutationFn: revokeContentSharedLinkRequest,
    onError: () => {
      handleMutateError('Failed to revoke the link')
    },
    onSuccess: () => {
      handleMutateSuccess('Link was successfully revoked')
    },
  })

  const introduceContentSharedLinkMutation = useMutation({
    mutationFn: introduceContentSharedLinkRequest,
    onError: () => {
      handleMutateError('Failed to introduce the link')
    },
    onSuccess: () => {
      handleMutateSuccess('Link was successfully introduced')
    },
  })

  const mutationLoading = useMemo<boolean>(
    () =>
      generateContentSharedLinkMutation.isLoading ||
      regenerateContentSharedLinkMutation.isLoading ||
      revokeContentSharedLinkMutation.isLoading ||
      introduceContentSharedLinkMutation.isLoading,
    [
      generateContentSharedLinkMutation.isLoading,
      introduceContentSharedLinkMutation.isLoading,
      regenerateContentSharedLinkMutation.isLoading,
      revokeContentSharedLinkMutation.isLoading,
    ]
  )

  return useMemo(
    () => ({
      sharedContentLinkResponse: query.data,
      getSharedContentLinkLoading: query.isFetching,
      generateContentSharedLinkMutation,
      regenerateContentSharedLinkMutation,
      revokeContentSharedLinkMutation,
      introduceContentSharedLinkMutation,
      mutationLoading,
    }),
    [
      generateContentSharedLinkMutation,
      introduceContentSharedLinkMutation,
      query.data,
      query.isFetching,
      regenerateContentSharedLinkMutation,
      revokeContentSharedLinkMutation,
      mutationLoading,
    ]
  )
}
